import styles from 'LandingSite/layout/Terms/Terms.module.scss';
import React, {Component} from 'react';

/**
 * Public privacy policy (acculex.io/privacy). Covers the whole Acculex platform and the Acculex ID
 * app, including biometric / face-data handling (Section 7) — this is the policy App Review checks.
 * Keep in step with the canonical source in the platform repo (docs/legal/PRIVACY_POLICY.md).
 */
class Privacy extends Component {

    componentDidMount() {
        window.scrollTo(0, 0)
    }

    render() {
        return (
            <div className={styles.terms}>
                <h1>Privacy Policy</h1>
                <p><em>Last updated: 9 July 2026</em></p>

                <p>This Privacy Policy explains how <strong>Acculex</strong> (a service of Claritas
                    Research Ltd — "Acculex", "we", "us", "our") collects, uses, discloses and protects
                    personal information when you use the <strong>Acculex platform</strong> at acculex.io
                    (accounts, workflows, document generation, electronic signature, the optional AI
                    assistant and related services) and the <strong>Acculex ID</strong> mobile app and
                    identity-verification service (together, the "Services"). It is written to meet the
                    UK GDPR and the Data Protection Act 2018, the EU GDPR, and — in the country-specific
                    sections at the end — the privacy laws of California and the wider United States,
                    Australia, New Zealand, Canada and Singapore.</p>

                <h3>1. Who is responsible for your data (controller / processor)</h3>
                <p>Acculex plays two roles depending on the data:</p>
                <ul>
                    <li><strong>Acculex as controller.</strong> For information about your use of the
                        Services themselves — your account, billing, security logs, support, our website
                        and marketing — Acculex is the data controller.</li>
                    <li><strong>Acculex as processor.</strong> When a business customer uses the Services
                        to create documents or to run an identity check on another person (for example an
                        employer verifying an employee), the customer decides the purpose and is the
                        controller, and Acculex processes the personal information on the customer's
                        behalf as a processor. If you are the subject of such a document or check and want
                        to exercise your rights, we will help, but the controller (our customer) is
                        primarily responsible and we will direct you to them where appropriate.</li>
                </ul>

                <h3>2. The personal information we collect</h3>
                <h5>a. Account and profile data</h5>
                <p>Name, email address, password (stored only as a secure hash), organisation, role,
                    preferences and, where you upgrade, your subscription status.</p>
                <h5>b. Content you create and provide</h5>
                <p>The information you enter into workflows to generate documents — which may include
                    names, addresses, dates of birth, job and pay details, company information, financial
                    figures and other details about you and about other people (for example an employee,
                    a counterparty, a shareholder). You are responsible for having a proper basis to
                    provide personal information about other people to us.</p>
                <h5>c. Identity-verification and biometric data</h5>
                <p>Where you complete an identity check through Acculex ID, we collect the data described
                    in Section 7 — including biometric (face) data. This is special-category / sensitive
                    data and Section 7 is the complete explanation of it.</p>
                <h5>d. Electronic-signature data</h5>
                <p>When you sign or send a document for signature, we record signing metadata to evidence
                    the signature — the signatory's name and email, timestamps, the document hash, and the
                    signer's IP address, device/user-agent and signing method — which is embedded in the
                    completion certificate.</p>
                <h5>e. Payment data</h5>
                <p>If you buy a subscription, our payment processor (Stripe) collects and processes your
                    card and billing details. Acculex does not store full card numbers; we keep your
                    subscription status and a payment reference.</p>
                <h5>f. Communications and support</h5>
                <p>The content of emails, support messages and — where enabled — SMS/messaging you
                    exchange with us, plus delivery and engagement metadata from our messaging providers.</p>
                <h5>g. AI-assistant interactions</h5>
                <p>If you use our optional in-app AI assistant, we process the messages and context you
                    provide to it (see Section 6).</p>
                <h5>h. Technical and usage data</h5>
                <p>IP address, device and browser information, log data, approximate location derived from
                    IP, pages/features used, and cookie or similar-technology identifiers (see Section 13).
                    Acculex ID may collect a precise device location only when a check includes a location
                    step, and only with permission.</p>
                <p>We do not intentionally collect special-category data other than the biometric data used
                    for identity verification (Section 7); please do not enter sensitive details into
                    free-text fields unless necessary for your document.</p>

                <h3>3. How and why we use your information, and our lawful bases</h3>
                <table className={styles.table}>
                    <thead>
                    <tr><th>Purpose</th><th>Lawful basis (UK/EU GDPR)</th></tr>
                    </thead>
                    <tbody>
                    <tr><td>Provide the Services (accounts, generate/store documents, run workflows)</td><td>Performance of a contract; legitimate interests</td></tr>
                    <tr><td>Identity verification (chip read, face match, liveness — Section 7)</td><td>Explicit consent for biometric data (Art 9(2)(a)); our customer's legal obligation / substantial public interest where applicable</td></tr>
                    <tr><td>Electronic signature and audit trail</td><td>Performance of a contract; legitimate interests</td></tr>
                    <tr><td>Payments and fraud prevention</td><td>Performance of a contract; legal obligation; legitimate interests</td></tr>
                    <tr><td>Optional AI assistant</td><td>Consent / performance of a contract</td></tr>
                    <tr><td>Communicate with you (service, security, support, marketing)</td><td>Legitimate interests; consent for marketing; legal obligation</td></tr>
                    <tr><td>Security, prevention of misuse, and audit</td><td>Legitimate interests; legal obligation</td></tr>
                    <tr><td>Improve and maintain the Services; comply with law</td><td>Legitimate interests; legal obligation</td></tr>
                    </tbody>
                </table>
                <p>Where we rely on legitimate interests, we have balanced them against your rights;
                    contact us for our assessment. Where we rely on consent, you can withdraw it at any
                    time (Section 12).</p>

                <h3>4. How your document content is used</h3>
                <p>Your documents are assembled to a professional standard from your answers, using our
                    templating and rules engine and our lawyer-curated content library. We do not use the
                    content of your documents to train machine-learning models, for advertising, or for any
                    purpose other than providing the Services to you and the security/legal purposes above.</p>

                <h3>5. Automated processing and decision-making</h3>
                <p>Document generation applies deterministic templates and rules to your answers — it does
                    not make decisions about people that produce legal or similarly significant effects. An
                    identity check produces a recommended outcome; where the result is inconclusive it is
                    routed to a human reviewer rather than auto-deciding, and a person can request human
                    review of any outcome that affects them (Section 12). We do not carry out
                    solely-automated decision-making with legal or similarly significant effects within the
                    meaning of Article 22 UK/EU GDPR without an applicable safeguard.</p>

                <h3>6. The AI assistant</h3>
                <p>Where the optional AI assistant is enabled, the messages and relevant context you
                    provide are sent to our AI model provider (Anthropic) to generate a response, under
                    terms that prohibit the provider from using your data to train its models. The
                    assistant is a support tool; it does not provide legal advice, and you should not rely
                    on it as a substitute for professional advice. Do not enter more personal or sensitive
                    information into it than necessary.</p>

                <h3>7. Biometric and identity-verification data (Acculex ID)</h3>
                <p>This section is the complete explanation of our collection, use, disclosure, sharing and
                    retention of face / biometric data.</p>
                <h5>What we collect for an identity check</h5>
                <ul>
                    <li><strong>Document chip data (NFC):</strong> the machine-readable zone (name,
                        document number, nationality, date of birth, expiry) and the signed security
                        object, used to confirm the document is genuine and unaltered; and the facial image
                        the issuing authority stored on the chip (data group DG2).</li>
                    <li><strong>Face data:</strong> a camera photo of the document photo page; a live
                        selfie; a short liveness video captured while you follow on-screen prompts; and, for
                        a non-chip document, photos of the document (whose portrait is used for the match).</li>
                    <li><strong>On-device face landmarks (transient):</strong> to run the liveness prompts,
                        the app measures simple facial geometry (eye openness, head angle) only in memory on
                        your device using Apple's Vision framework; these measurements are never transmitted
                        or stored.</li>
                    <li><strong>Face embedding (server-side):</strong> to compare two faces we convert each
                        face image into a mathematical vector (an "embedding") and measure its similarity.</li>
                    <li><strong>Device location</strong> (only if the check includes a location step) and a
                        <strong> proof-of-address document</strong> (only if it includes an address step).</li>
                </ul>
                <h5>How we use it — and only this</h5>
                <p>To match the live selfie against the document portrait; to run a liveness / anti-spoofing
                    check; and to let an authorised human reviewer assess a borderline or failed result. We
                    do not use biometric data to identify you in any other context, to build or enrich any
                    facial database, for advertising or profiling, or to train any model.</p>
                <h5>What leaves your device, and where it is processed</h5>
                <p>The on-device liveness measurements never leave your device. To perform the checks, your
                    selfie, liveness frames and the document/chip data are sent over an encrypted (TLS)
                    connection to Acculex's own servers, where the checks run. Your device does not decide
                    pass/fail.</p>
                <h5>Sharing — no third-party face service</h5>
                <p>Face detection, the embedding and the face match are performed entirely on Acculex's own
                    servers using Acculex's own models. Your biometric data is not shared with, or processed
                    by, any third-party face-recognition or biometric service. Because the check is
                    performed for the organisation that requested it, the result (and, where a human review
                    is needed, the relevant images) is made available to authorised people at that
                    organisation.</p>
                <h5>Explicit consent</h5>
                <p>Because this is special-category biometric data, we ask for your explicit, informed
                    consent in the app before any capture begins, with a link to this Policy, and you can
                    decline or withdraw consent (in which case we cannot complete the check).</p>
                <h5>Retention</h5>
                <p>The raw biometric images and any embeddings are deleted promptly after the check is
                    completed and any review is finished, and in any event within 30 days; we then keep only
                    the outcome and minimal identity fields needed as evidence, for as long as the
                    requesting organisation must retain evidence of the check, after which they are deleted.
                    You can ask us to delete your data sooner (Section 12).</p>

                <h3>8. Who we share information with</h3>
                <ul>
                    <li>Your organisation / the requesting party — the controller who asked for the document
                        or check.</li>
                    <li>Service providers (sub-processors) acting on our instructions under contract,
                        including: <a target="_blank" rel="noopener noreferrer" href="https://stripe.com/gb">Stripe</a> (payments);
                        our email provider (Mailjet) and, where enabled, an SMS/messaging provider (Twilio);
                        our AI provider (Anthropic) where the AI assistant is used; and reputable
                        cloud/hosting providers on whose infrastructure our servers run.</li>
                    <li>No third-party face/biometric processor — see Section 7.</li>
                    <li>Professional advisers, auditors, insurers, and, in a corporate transaction, a buyer
                        of the business, under confidentiality.</li>
                    <li>Authorities and others where required by law, to enforce our terms, or to protect
                        rights, safety and security.</li>
                </ul>
                <p>We do not sell your personal information, and we do not share it for cross-context
                    behavioural advertising.</p>

                <h3>9. International transfers</h3>
                <p>Our servers are located in the UK/EEA. Where we transfer personal information outside the
                    UK/EEA, we rely on an adequacy decision or appropriate safeguards such as the UK
                    International Data Transfer Agreement / Addendum or the EU Standard Contractual Clauses,
                    with a transfer risk assessment where required. Contact us for details or a copy of the
                    safeguards.</p>

                <h3>10. How long we keep information (retention)</h3>
                <ul>
                    <li>Account data — for the life of your account and a limited period afterwards.</li>
                    <li>Documents and workflow content — for as long as your account keeps them or the
                        controller requires, then deleted or returned.</li>
                    <li>Biometric/identity data — as in Section 7 (raw biometrics deleted within 30 days;
                        outcome retained per the controller's obligation).</li>
                    <li>Signature/audit and financial records — for the periods required by law (for example
                        tax and limitation periods).</li>
                    <li>Logs and security data — for a limited period proportionate to the security purpose.</li>
                </ul>
                <p>When no longer needed, information is securely deleted or irreversibly anonymised.</p>

                <h3>11. How we protect information</h3>
                <p>We use technical and organisational measures appropriate to the risk, including
                    encryption in transit, hashed credentials, access controls and least-privilege,
                    isolation of sensitive data, logging and monitoring, and staff confidentiality. No
                    system is perfectly secure, but we work to protect your information and to meet our
                    breach-notification obligations.</p>

                <h3>12. Your rights</h3>
                <p>Subject to your local law, you may have the right to access your data; correct it; erase
                    it; restrict or object to processing (including direct marketing); data portability;
                    withdraw consent; and not be subject to solely-automated decisions with legal or
                    similarly significant effects without a safeguard (including a right to human review of
                    an identity-check outcome). To exercise these rights contact <a href="mailto:privacy@acculex.io">privacy@acculex.io</a>.
                    We will verify your identity and respond within the time the law requires. You can also
                    complain to your data-protection authority (in the UK, the Information Commissioner's
                    Office, ico.org.uk); we would appreciate the chance to help first.</p>

                <h3>13. Cookies and similar technologies</h3>
                <p>We use strictly-necessary cookies/local storage to sign you in and keep the Services
                    secure, and — with your consent where required — analytics or preference cookies. You
                    can control non-essential cookies through our cookie banner (where shown) or your
                    browser settings. Disabling essential cookies may stop the Services working.</p>

                <h3>14. Children</h3>
                <p>The Services are intended for business users and adults. They are not directed at
                    children, and we do not knowingly collect children's data. Acculex ID is intended for
                    adults completing an identity check.</p>

                <h3>15. Changes to this Policy</h3>
                <p>We will update this Policy when our practices or the law change and will revise the "Last
                    updated" date. Material changes will be notified through the Services or by email.</p>

                <h3>16. Contact us</h3>
                <p>Acculex is a service of <strong>Claritas Research Ltd</strong>, a company registered in
                    England and Wales (company number 10119402), registered office: 1 Aquarelle House,
                    City Road, London, United Kingdom, EC1V 1AG.</p>
                <p>Privacy and data protection contact: Alexander Thornton
                    — <a href="mailto:alex@acculex.io">alex@acculex.io</a>, or <a
                        href="mailto:privacy@acculex.io">privacy@acculex.io</a>.</p>

                <h1 style={{marginTop: '48px'}}>Region-specific information</h1>
                <p>The following supplements the Policy above for people in these places; where it conflicts
                    with the above, the region-specific terms govern for those people.</p>

                <h3>United Kingdom &amp; EEA (UK/EU GDPR)</h3>
                <p>The controller/processor split is in Section 1; lawful bases are in Section 3; your
                    rights are in Section 12; transfers are in Section 9. Our lawful basis for biometric
                    data is your explicit consent (Art 9(2)(a)); a business customer may additionally rely
                    on a legal obligation or substantial public interest. You may complain to your national
                    supervisory authority or the ICO.</p>

                <h3>California (CCPA/CPRA)</h3>
                <p>We may collect these categories: identifiers; commercial information; internet/network
                    activity; geolocation; sensitive personal information (government-ID and biometric
                    information for identity verification, and account credentials); and professional
                    information — for the business purposes in Section 3. We do not "sell" or "share" (for
                    cross-context behavioural advertising) personal information, and we do not use sensitive
                    personal information beyond the purposes permitted by the CPRA. California residents may
                    know/access, delete, correct, and limit the use of sensitive personal information, and
                    are free from discrimination for exercising these rights; you may use an authorised
                    agent. Contact <a href="mailto:privacy@acculex.io">privacy@acculex.io</a>.</p>

                <h3>Illinois and other US biometric-privacy laws (e.g. BIPA)</h3>
                <p>Where US state biometric laws apply, we collect biometric identifiers only after
                    informing you and obtaining your written consent; use them solely for identity
                    verification; do not sell, lease, trade or profit from them; store them securely; and
                    retain them no longer than needed, destroying them within the periods in Sections 7 and
                    10 and in any event within the statutory maximum. We do not disclose biometric data to
                    third parties except as in Section 8 (which does not include any third-party face
                    service) or as required by law.</p>

                <h3>Australia (Privacy Act 1988 / APPs)</h3>
                <p>We handle personal information in accordance with the Australian Privacy Principles.
                    Biometric information is sensitive information collected only with your consent and for
                    identity verification. You may access and correct your information and complain to the
                    Office of the Australian Information Commissioner (oaic.gov.au). Before disclosing
                    personal information overseas we take reasonable steps consistent with APP 8.</p>

                <h3>New Zealand (Privacy Act 2020)</h3>
                <p>We comply with the Information Privacy Principles. You may access and correct your
                    information and complain to the Office of the Privacy Commissioner (privacy.org.nz).
                    Before disclosing personal information overseas we take reasonable steps to ensure
                    comparable protection.</p>

                <h3>Canada (PIPEDA)</h3>
                <p>We obtain meaningful consent for collecting, using and disclosing personal information
                    (express consent for sensitive biometric data), limit use to the identified purposes,
                    and let you access and correct your information. You may complain to us and then to the
                    Office of the Privacy Commissioner of Canada (priv.gc.ca).</p>

                <h3>Singapore (PDPA)</h3>
                <p>We collect, use and disclose personal data with consent and for the purposes notified,
                    protect it, and let you access and correct it. Our Data Protection Officer can be
                    reached at <a href="mailto:privacy@acculex.io">privacy@acculex.io</a>. Before
                    transferring personal data outside Singapore we take steps to ensure a comparable
                    standard of protection.</p>
            </div>
        )
    }
}

export default Privacy;
